// Pure diff / merge for "Re-extract from spec sheet" (AdminProductEdit).
//
// products.clearances is a single JSONB document that holds more than what
// extract-specs returns:
//   - top-level flat fields (rear_mm … height_mm) + "original" {value, unit}
//   - per-standard sets under "standards".<KEY> (e.g. CSA_B365 / NFPA_211),
//     each with its own mm fields and "original" (migration 20261014000500)
//   - any other keys other tools wrote (notes, floor_protection_radius_mm, …)
// Re-extraction only knows about the flat fields. Writing its output over the
// column would silently destroy everything else, so instead we MERGE: only
// fields the extraction actually returned, only those the admin accepted, and
// nothing else in the document is touched.
//
// No React / Supabase imports — unit-tested by clearanceMerge.test.ts.

export type OriginalUnit = "in" | "mm";
export interface OriginalValue {
  value: number;
  unit: OriginalUnit;
}

export type ClearanceDoc = Record<string, unknown>;

/** The fields extract-specs returns, in display order. */
export const MERGE_FIELDS: ReadonlyArray<{ key: string; label: string }> = [
  { key: "rear_mm", label: "Rear" },
  { key: "side_mm", label: "Side" },
  { key: "front_mm", label: "Front" },
  { key: "ceiling_mm", label: "Ceiling" },
  { key: "floor_mm", label: "Floor" },
  { key: "width_mm", label: "Product width" },
  { key: "depth_mm", label: "Product depth" },
  { key: "height_mm", label: "Product height" },
];

export interface ClearanceCell {
  mm: number | null;
  original: OriginalValue | null;
}

export interface DiffRow {
  key: string;
  label: string;
  current: ClearanceCell;
  extracted: ClearanceCell;
  /** "new": no current value · "changed": mm or printed value/unit differs · "unchanged" */
  kind: "new" | "changed" | "unchanged";
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function readMm(doc: unknown, key: string): number | null {
  if (!isRecord(doc)) return null;
  const v = doc[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Well-formed original for `key`, else null (mirrors specReader.clearanceOriginal). */
export function readOriginal(doc: unknown, key: string): OriginalValue | null {
  if (!isRecord(doc) || !isRecord(doc.original)) return null;
  const o = doc.original[key];
  if (!isRecord(o)) return null;
  if (typeof o.value !== "number" || !(o.value > 0)) return null;
  if (o.unit !== "in" && o.unit !== "mm") return null;
  return { value: o.value, unit: o.unit };
}

function sameOriginal(a: OriginalValue | null, b: OriginalValue | null): boolean {
  if (!a || !b) return a === b;
  return a.unit === b.unit && Math.abs(a.value - b.value) < 1e-9;
}

/**
 * One row per field the extraction RETURNED (a non-null mm). A field the
 * extraction left null is "not found on the sheet", not "should be empty", so it
 * produces no row and the current value stays.
 */
export function diffClearances(current: unknown, extracted: unknown): DiffRow[] {
  const rows: DiffRow[] = [];
  for (const { key, label } of MERGE_FIELDS) {
    const exMm = readMm(extracted, key);
    if (exMm === null) continue;
    const cur: ClearanceCell = { mm: readMm(current, key), original: readOriginal(current, key) };
    const ex: ClearanceCell = { mm: exMm, original: readOriginal(extracted, key) };
    const kind: DiffRow["kind"] =
      cur.mm === null
        ? "new"
        : cur.mm === ex.mm && sameOriginal(cur.original, ex.original)
          ? "unchanged"
          : "changed";
    rows.push({ key, label, current: cur, extracted: ex, kind });
  }
  return rows;
}

/** Keys the admin can meaningfully accept (everything that is not "unchanged"). */
export function changedKeys(rows: DiffRow[]): string[] {
  return rows.filter((r) => r.kind !== "unchanged").map((r) => r.key);
}

/**
 * Returns a NEW document: `current` with only the accepted, extraction-returned
 * fields applied. Never mutates its inputs. Everything not named — "standards"
 * and its per-standard "original" entries, other top-level keys, the "original"
 * entries of fields that were not accepted — is carried over untouched.
 *
 * `accepted` omitted ⇒ accept every changed field ("accept all").
 */
export function mergeClearances(
  current: unknown,
  extracted: unknown,
  accepted?: ReadonlyArray<string>,
): ClearanceDoc {
  const base: ClearanceDoc = isRecord(current) ? (JSON.parse(JSON.stringify(current)) as ClearanceDoc) : {};
  const rows = diffClearances(current, extracted);
  const allow = accepted ? new Set(accepted) : null;

  for (const row of rows) {
    if (row.kind === "unchanged") continue;
    if (allow && !allow.has(row.key)) continue;

    base[row.key] = row.extracted.mm;

    const original: Record<string, unknown> = isRecord(base.original) ? { ...base.original } : {};
    if (row.extracted.original) {
      original[row.key] = { value: row.extracted.original.value, unit: row.extracted.original.unit };
    } else {
      // New mm with no printed value: the old "original" no longer describes
      // this number (migration 20261014000500 treats such entries as stale).
      delete original[row.key];
    }
    if (Object.keys(original).length > 0) base.original = original;
    else delete base.original;
  }
  return base;
}

/** "16 in" / "150 mm" — same format as specReader.formatClearanceOriginal. */
export function formatOriginal(o: OriginalValue): string {
  return `${Number(o.value.toFixed(4))} ${o.unit}`;
}
